<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { markRaw } from 'vue';
import resources from '@enums/resources';
import vuexForm from '@enums/vuex-form';
import customComponents from '@enums/custom-components';
import featuresFlag from '@enums/feature-flags';
import valueTypes from '@enums/value-types';
import { bind, get } from 'lodash';
import validators from '@/js/validators';
import { getDefaultValuePropName, getDefaultValuePropValue } from '@/js/utils/component-utils';
import commonFormDialogComponent from './common-form-dialog.vue';
import i18n from '@/js/vue-i18n';

const campaigns = resources.campaigns;
const { canEditCampaign, canPublishCampaigns } = featuresFlag;
const { componentTypes } = customComponents;
const { fieldTypes, sourceTypes } = vuexForm;
const { object } = valueTypes;

const createForm = function({
    isEditable,
    isDetailsOpened,
    isStoreGroupsOpened,
    onDetailsPanelStateChange,
    isPrivateDefault,
    campaignDefaults,
    categoriesTree,
    updateCategoriesInStagingArea,
    storeGroupsChangeHandler,
}) {
    return {
        vuexModule: 'campaigns',
        editable: true,
        addEvent: 'createCampaign',
        editEvent: 'updateCampaign',
        fields: [
            {
                fieldName: 'name',
                label: 'planning.createCampaign.title',
                type: fieldTypes.text,
                placeholder: 'planning.createCampaign.titlePlaceholder',
                cssClass: 'vuex-form__name',
                isRequired: true,
                editable: isEditable,
                dataIdE2E: 'fld-campaign-title',
                validations: [
                    {
                        message: i18n.t('validation.campaign.nameRequired'),
                        validator: validators.required,
                    },
                    // example validation with params
                    // {
                    //     message: i18n.t('validation.campaign.nameMaxLength'),
                    //     validator: validators.maxLength,
                    //     params: [50],
                    // },
                ],
            },
            {
                fieldName: 'briefing',
                label: 'planning.createCampaign.briefing',
                type: fieldTypes.textarea,
                placeholder: 'planning.createCampaign.briefingPlaceholder',
                cssClass: 'vuex-form__briefing',
                editable: isEditable,
                dataIdE2E: 'fld-campaign-briefing',
            },
            {
                label: 'planning.createCampaign.dates',
                type: fieldTypes.dateRange,
                cssClass: 'vuex-form__dates',
                isRequired: true,
                editable: isEditable,
                minDate: {
                    source: sourceTypes.context,
                    path: 'startDate',
                },
                from: {
                    fieldName: 'startDate',
                },
                to: {
                    fieldName: 'endDate',
                },
                entity: 'campaign',
                dataIdE2E: 'fld-campaign-dates',
                validations: [
                    {
                        message: i18n.t('validation.campaign.datesRequired'),
                        validator: validators.required,
                    },
                    {
                        validator: validators.dateRangeRequired,
                    },
                ],
            },
            {
                type: componentTypes.expansionPanel,
                isContainer: true,
                label: 'planning.createCampaign.additionalDetails',
                cssClass: 'vuex-form__details',
                expansionPanelClass: 'details__wrapper',
                isOpenState: isDetailsOpened,
                change: onDetailsPanelStateChange,
                editable: isEditable,
                elvation: 0,
                flat: true,
                children: [
                    {
                        fieldName: 'promoResources',
                        type: fieldTypes.iconCheckboxesList,
                        cssClass: 'vuex-form__promo-resources',
                        isRequired: true,
                        editable: isEditable,
                        label: 'planning.promoResources',
                        options: {
                            source: sourceTypes.getter,
                            identifier: 'getCheckboxListOptions',
                            module: 'campaigns',
                            params: {
                                attributeName: 'resources',
                                attributeKey: 'type',
                                resourceOptionAttributeKey: 'key',
                                resource: 'clientConfig',
                                getOptionsFunction: 'getPromoResources',
                                campaignId: {
                                    source: sourceTypes.editContext,
                                    path: '_id',
                                },
                            },
                        },
                        itemText: 'description',
                        itemValue: 'key',
                        valueType: object,
                        [getDefaultValuePropName(
                            campaignDefaults.promoResources
                        )]: getDefaultValuePropValue(campaignDefaults.promoResources, {
                            source: sourceTypes.getter,
                            identifier: 'getPromoResources',
                            module: 'clientConfig',
                        }),
                        validations: [
                            {
                                validator: validators.required,
                            },
                        ],
                    },
                    {
                        fieldName: 'categories',
                        type: componentTypes.categoriesSelect,
                        tree: categoriesTree,
                        cssClass: 'vuex-form__categories',
                        editable: isEditable,
                        isRequired: true,
                        update: updateCategoriesInStagingArea,
                        [getDefaultValuePropName(
                            campaignDefaults.categories
                        )]: getDefaultValuePropValue(campaignDefaults.categories, {
                            source: sourceTypes.getter,
                            identifier: 'userCategoryKeys',
                            module: 'context',
                        }),
                    },
                    {
                        fieldName: 'customerGroups',
                        type: fieldTypes.checkboxesList,
                        cssClass: 'vuex-form__customer-restrictions',
                        isRequired: true,
                        editable: isEditable,
                        label: 'planning.customerRestrictions',
                        options: {
                            source: sourceTypes.getter,
                            identifier: 'getCheckboxListOptions',
                            module: 'campaigns',
                            params: {
                                attributeName: 'customerGroups',
                                attributeKey: 'key',
                                resource: 'clientConfig',
                                getOptionsFunction: 'getCustomerRestrictions',
                                campaignId: {
                                    source: sourceTypes.editContext,
                                    path: '_id',
                                },
                            },
                        },
                        itemText: 'description',
                        itemValue: 'key',
                        valueType: object,
                        [getDefaultValuePropName(
                            campaignDefaults.customerRestrictions
                        )]: getDefaultValuePropValue(campaignDefaults.customerRestrictions, {
                            source: sourceTypes.getter,
                            identifier: 'getCustomerRestrictions',
                            module: 'clientConfig',
                        }),
                        validations: [
                            {
                                validator: validators.required,
                            },
                        ],
                    },
                ],
            },
            {
                type: componentTypes.expansionPanel,
                isContainer: true,
                label: 'planning.createCampaign.storeGroups',
                cssClass: 'vuex-form__store-groups',
                isOpenState: isStoreGroupsOpened,
                editable: isEditable,
                elvation: 0,
                flat: true,
                children: [
                    {
                        fieldName: 'storeGroups',
                        type: fieldTypes.select,
                        cssClass: 'vuex-form__store-groups-selector',
                        placeholder: 'planning.createCampaign.storeGroupsPlaceholder',
                        isRequired: true,
                        autocomplete: true,
                        multiple: true,
                        deletableChips: true,
                        chips: true,
                        editable: isEditable,
                        returnObject: true,
                        finiteList: true,
                        changeHandler: storeGroupsChangeHandler,
                        options: {
                            source: sourceTypes.getter,
                            identifier: 'getCheckboxListOptions',
                            module: 'campaigns',
                            params: {
                                attributeName: 'storeGroups',
                                attributeKey: 'key',
                                resource: 'storeGroups',
                                getOptionsFunction: 'getAvailableStoreGroupsOptions',
                                startDate: { source: sourceTypes.editContext, path: 'startDate' },
                                selectedStoreGroups: {
                                    source: sourceTypes.editContext,
                                    path: 'storeGroups',
                                },
                                getUserAccessOptionsMap: 'userStoreGroupsMap',
                                campaignId: {
                                    source: sourceTypes.editContext,
                                    path: '_id',
                                },
                                useReference: false,
                            },
                        },
                        itemText: 'description',
                        itemValue: 'key',
                        valueType: object,
                        validations: [
                            {
                                validator: validators.required,
                            },
                        ],
                    },
                ],
            },
            {
                fieldName: 'isPrivate',
                defaultValue: isPrivateDefault,
                editable: isEditable,
            },
        ],
    };
};

export default {
    extends: commonFormDialogComponent,

    props: {
        preparationView: {
            type: Boolean,
            default: false,
        },
    },

    data: () => ({
        resource: campaigns,
        btnCancelId: 'campaignCancelId',
        btnConfirmDialogCancel: 'campaignConfirmDialogCancelId',
        btnConfirmId: 'campaignBtnConfirmId',
        isDetailsOpened: null,
        hasConfirmationDialog: false,
    }),

    computed: {
        ...mapState('clientConfig', ['toggleLogic', 'additionalDetailsDefaultSelection']),
        ...mapGetters('hierarchy', ['getCategoriesTree']),
        ...mapGetters('campaigns', ['getSubCampaignsCategories', 'getStagingAreaField']),

        form() {
            return markRaw(
                createForm({
                    isEditable: !!this.toggleLogic[canEditCampaign] && !this.isReadOnly,
                    isDetailsOpened: this.isDetailsOpened,
                    isStoreGroupsOpened: 0,
                    onDetailsPanelStateChange: bind(
                        this.onExpansionPanelStateChange,
                        this,
                        'isDetailsOpened'
                    ),
                    isPrivateDefault: !!this.toggleLogic[canPublishCampaigns],
                    campaignDefaults: this.additionalDetailsDefaultSelection.campaign,
                    categoriesTree: this.getCategoriesTree({
                        selectedCategories: this.getStagingAreaField({
                            namespace: this.editContext ? this.editContext._id : 'default',
                            fieldName: 'categories',
                        }),
                        childCategories: this.getSubCampaignsCategories({
                            campaignId: get(this.editContext, '_id'),
                        }),
                        isEditable: !!this.toggleLogic[canEditCampaign] && !this.isReadOnly,
                    }),
                    updateCategoriesInStagingArea: this.updateCategoriesInStagingArea,
                    storeGroupsChangeHandler: this.storeGroupsChangeHandler,
                })
            );
        },
        labels() {
            return {
                btnText: 'planning.createCampaign.createButton',
                heading: this.editMode
                    ? 'planning.editCampaign.heading'
                    : 'planning.createCampaign.heading',
                planCampaign: 'planning.planCampaign',
            };
        },
    },

    methods: {
        ...mapActions('campaigns', ['setStagingAreaField']),
        ...mapActions('rateCards', ['fetchRateCardsByStoreGroupsAndDates']),

        setModalExpansionPanelsState() {
            this.setExpansionPanelState('isDetailsOpened', [
                'customerGroups',
                'categories',
                'promoResources',
            ]);
        },

        planOutCampaign() {
            this.selectCampaignSingleView();
        },
        storeGroupsChangeHandler() {
            this.fetchRateCardsByStoreGroupsAndDates({
                subCampaignDetails: {
                    storeGroups: this.getStagingAreaField({
                        namespace: this.editContext ? this.editContext._id : 'default',
                        fieldName: 'storeGroups',
                    }),
                    startDate: this.getStagingAreaField({
                        namespace: this.editContext ? this.editContext._id : 'default',
                        fieldName: 'startDate',
                    }),
                    endDate: this.getStagingAreaField({
                        namespace: this.editContext ? this.editContext._id : 'default',
                        fieldName: 'endDate',
                    }),
                },
            });
        },
        updateCategoriesInStagingArea(selectedCategories) {
            const namespace = this.editContext ? this.editContext._id : 'default';

            this.setStagingAreaField({
                namespace,
                fieldName: 'categories',
                value: selectedCategories,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.campaign-container {
    color: $promo-text-colour;
    max-width: 83rem;

    :deep(.vuex-form) {
        display: grid;
        grid-template:
            'title description dates'
            'link description workflow'
            'details details details'
            'store-groups store-groups store-groups'
            'nominations nominations nominations '
            'tags tags tags'
            'children children children'
            'rate-cards rate-cards rate-cards'
            / 1fr 1fr 1fr;
    }

    :deep(.vuex-form__dates) {
        grid-area: dates;
        padding-top: 1rem;
    }

    :deep(.vuex-form__link) {
        grid-area: link;
    }

    :deep(.vuex-form__workflow) {
        grid-area: workflow;
    }

    :deep(.vuex-form__details) {
        grid-area: details;
        border-top: $component-border-divider;
        margin: 0 !important;

        .details {
            &__wrapper {
                display: grid;
                grid-template:
                    'categories . . regions'
                    'promo-resources custom-restrictions . .';
            }
        }
    }

    :deep(.vuex-form__nominations) {
        grid-area: nominations;
        border-top: $component-border-divider;
        margin: 0 !important;
        padding-bottom: 1rem;
    }

    :deep(.vuex-form__tags) {
        grid-area: tags;
        border-top: $component-border-divider;
        margin: 0 !important;
        padding-bottom: 1rem;
    }

    :deep(.vuex-form__children) {
        grid-area: children;
        border-top: $component-border-divider;
        margin: 0 !important;
        padding-bottom: 1rem;
    }

    :deep(.vuex-form__rate-cards) {
        grid-area: rate-cards;
        border-top: $component-border-divider;
        margin: 0 !important;
        padding-bottom: 1rem;
    }

    :deep(.vuex-form__name) {
        grid-area: title;
        padding-right: 2rem;
        width: 26.5rem;
    }

    :deep(.vuex-form__briefing) {
        grid-area: description;
        padding-right: 2rem;
        width: 38.5rem;

        .v-textarea {
            .v-field__input {
                border: 1px solid #e2e2e2;
                font-size: 1.2rem;
                padding: 1.1rem 1.3rem 1.1rem 1rem;
                opacity: 1;
                mask-image: none;

                &:focus {
                    border-color: #969696;
                }

                &::placeholder {
                    color: #969696;
                    font-size: 1.2rem;
                    opacity: 1;
                }
            }
            .v-field__overlay,
            .v-field__outline {
                display: none;
            }
        }
    }

    :deep(.vuex-form .rtls-expansion-panel-content) {
        .v-expansion-panel-text__wrapper {
            display: contents;
        }
    }

    :deep(.vuex-form__store-groups) {
        grid-area: store-groups;
        border-top: $component-border-divider;
        margin: 0 !important;
        padding-bottom: 1rem;
    }

    :deep(.vuex-form__store-groups-selector) {
        display: flex;
        min-width: 50%;
        max-width: 100%;
    }

    :deep(.vuex-form__promo-resources) {
        grid-area: promo-resources;
        padding-bottom: 1.4rem;

        .v-selection-control__wrapper {
            max-width: 3.3rem;
        }

        .icon-checkboxes-list-container__control {
            padding: 0;
            display: flex;
            position: relative;
            border-right: $component-border-divider;

            .v-input--checkbox.error--text {
                padding-bottom: 1rem;
            }

            .v-messages.error--text {
                position: absolute;
                top: 2.5rem;
                left: 0;
            }
        }
    }

    :deep(.vuex-form__categories) {
        grid-area: categories;
        grid-column: 1 / span 2;

        .select-all {
            .v-selection-control__wrapper {
                width: 2.4rem;
            }
        }
    }

    :deep(.vuex-form__customer-restrictions) {
        grid-area: custom-restrictions;
        padding: 0;

        .v-selection-control__wrapper {
            width: 2.4rem;
        }

        .checkboxes-list-container__control {
            display: flex;
            position: relative;

            .v-messages.error--text {
                position: absolute;
                top: 2rem;
                left: 0;
            }

            .rtls-checkbox {
                margin-right: 1.5rem;
            }
        }
    }

    &__bottom-container {
        display: flex;
        align-items: center;

        .author-container {
            display: flex;
            justify-content: space-between;
            line-height: 2.1rem;
            font-size: 1.2rem;
            font-weight: 600;

            & > p {
                margin-right: 1.3rem;
            }
        }
        .v-divider {
            margin-right: 1.3rem;
            margin-left: 1.3rem;
        }

        &-right {
            display: flex;
            margin-left: auto;
        }
    }

    &__author-avatar {
        background-color: $promo-text-colour;
        font-size: 1rem;
        font-weight: 400;
    }

    &__last-updated {
        font-size: 1.2rem;
        font-weight: 600;
        .date {
            font-weight: 400;
        }
    }

    .white--text {
        color: $promo-white;
    }
}
</style>
